import React, { useState } from 'react';
import type { BulkAction, CandidateNote } from './bulk_action';
import styled from 'styled-components';
import { colors, fontWeight } from '@grnhse/seedling/lib/azalea/constants';
import {
  CheckMarkCircle,
  ErrorIcon,
  PendingIcon,
  NeutralIcon,
} from '@grnhse/seedling/lib/azalea/icons';
// @ts-expect-error - TS7016 - Could not find a declaration file for module 'rc-progress'. 'node_modules/rc-progress/lib/index.js' implicitly has an 'any' type.
import { Circle } from 'rc-progress';
import t from 'shared/utils/translation';
import { CSSTransitionGroup } from 'react-transition-group';
// @ts-expect-error - TS7016 - Could not find a declaration file for module '@grnhse/seedling/lib/azalea/components/loading_spinner'. 'node_modules/@grnhse/seedling/lib/azalea/components/loading_spinner.js' implicitly has an 'any' type.
import LoadingSpinner from '@grnhse/seedling/lib/azalea/components/loading_spinner';
import { isUnresolved } from './bulk_action';
import { FullPalette } from '@grnhse/seedling/lib/birch/colors';

type Props = {
  bulkAction: BulkAction;
  cancelAction: (arg1: number) => Promise<any>;
};

const ICON_COLORS = {
  preparing: colors.mediumGrey,
  executing: colors.mediumGrey,
  complete: colors.mediumGreenhouseGreen,
  failed: colors.darkRed,
  cancelled: colors.mediumGrey,
  complete_with_errors: colors.darkYellow,
} as const;

const ICONS = {
  preparing: PendingIcon,
  executing: null,
  complete: CheckMarkCircle,
  failed: ErrorIcon,
  cancelled: ErrorIcon,
  complete_with_errors: NeutralIcon,
} as const;

const Row = ({ bulkAction, cancelAction }: Props) => {
  const [showDetails, setShowDetails] = useState(false);
  let iconComponent = null;

  if (bulkAction.status === 'executing') {
    if (bulkAction.totalCount < 20) {
      iconComponent = <LoadingSpinner size="small" />;
    } else {
      iconComponent = (
        <Circle
          percent={bulkAction.progress}
          strokeWidth={10}
          strokeColor={colors.mediumGreenhouseGreen}
          trailWidth={5}
          trailColor={colors.lightGreenhouseGreen}
          width={20}
          height={20}
        />
      );
    }
  } else {
    const Icon = ICONS[bulkAction.status];
    iconComponent = <Icon color={ICON_COLORS[bulkAction.status]} />;
  }

  return (
    <Container>
      <CSSTransitionGroup
        transitionName="icon"
        transitionLeaveTimeout={500}
        transitionEnterTimeout={500}
      >
        <IconContainer key={bulkAction.status}>
          <IconPlacement>{iconComponent}</IconPlacement>
        </IconContainer>
      </CSSTransitionGroup>

      <Details>
        {/* @ts-expect-error - TS2769 - No overload matches this call. */}
        <Status status={bulkAction.status}>
          <StatusName data-provides="bulk-actions-tray-status-name">
            {t(`bulk_actions.tray.status.${bulkAction.status}`)}
          </StatusName>
          {bulkAction.status === 'complete_with_errors' &&
            !!bulkAction.failureCount && (
              <StatusNote>
                {' • ' +
                  t('bulk_actions.tray.error_count', {
                    count: bulkAction.failureCount,
                  })}
              </StatusNote>
            )}
        </Status>
        <Name data-provides="bulk-actions-tray-name">{bulkAction.name}</Name>
        {(!!bulkAction.skipped?.length || !!bulkAction.warnings?.length) && (
          <>
            <ToggleDetails
              href="#"
              data-provides="bulk-actions-tray-toggle-details"
              onClick={(event: React.MouseEvent) => {
                event.preventDefault();
                setShowDetails(!showDetails);
              }}
            >
              {showDetails
                ? t('bulk_actions.tray.details.hide')
                : t('bulk_actions.tray.details.show')}
            </ToggleDetails>
            {showDetails && (
              <>
                <CandidateNotes
                  heading={t('bulk_actions.tray.details.skipped')}
                  notes={bulkAction.skipped}
                  dataProvides="bulk-actions-tray-skipped"
                />
                <CandidateNotes
                  heading={t('bulk_actions.tray.details.warnings')}
                  notes={bulkAction.warnings}
                  dataProvides="bulk-actions-tray-warnings"
                />
              </>
            )}
          </>
        )}
      </Details>

      {isUnresolved(bulkAction) && (
        <Cancel onClick={() => cancelAction(bulkAction.id)} href="#">
          {t('bulk_actions.tray.cancel')}
        </Cancel>
      )}
    </Container>
  );
};

const CandidateNotes = ({
  heading,
  notes,
  dataProvides,
}: {
  heading: string;
  notes?: Array<CandidateNote>;
  dataProvides: string;
}) => {
  if (!notes?.length) {
    return null;
  }

  return (
    <>
      <NotesHeading>{heading}</NotesHeading>
      <NotesList data-provides={dataProvides}>
        {notes.map(({ name, reason }, index) => (
          <li key={index}>{`${name}: ${reason}`}</li>
        ))}
      </NotesList>
    </>
  );
};

export default Row;

const Container = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-start;
`;

const Cancel = styled.a`
  align-self: center;
  color: ${FullPalette.GREEN_700};
  font-size: 0.875rem;
  text-decoration: none;

  &:hover {
    color: ${FullPalette.EVERGREEN_700};
  }
`;

const IconPlacement = styled.div`
  position: absolute;
  top: 0;
  left: 0;
`;

const IconContainer = styled.div`
  margin-right: 15px;
  width: 20px;
  position: relative;

  transition: opacity 500ms;

  &.icon-leave,
  &.icon-enter.icon-enter-active {
    opacity: 1;
  }

  &.icon-leave.icon-leave-active,
  &.icon-enter {
    opacity: 0;
  }
`;

const Details = styled.div`
  width: 190px;
  margin-right: 15px;
`;

const Status = styled.div`
  font-size: 13px;
  font-weight: ${fontWeight.normal};
  color: ${colors.darkGrey};
`;

const StatusName = styled.span``;

const StatusNote = styled.span``;

const ToggleDetails = styled.a`
  font-size: 13px;
  color: ${FullPalette.GREEN_700};
  text-decoration: none;

  &:hover {
    color: ${FullPalette.EVERGREEN_700};
  }
`;

const NotesHeading = styled.div`
  margin-top: 4px;
  font-size: 13px;
  font-weight: ${fontWeight.heavy};
  color: ${colors.darkGrey};
`;

const NotesList = styled.ul`
  list-style: none;
  margin: 4px 0 0;
  padding: 0;
  max-height: 160px;
  overflow-y: auto;
  font-size: 13px;
  color: ${colors.darkGrey};
`;

const Name = styled.div`
  font-size: 14px;
  font-weight: ${fontWeight.normal};
`;
